import _ from 'lodash';

export enum MessagingAuthorType {
    SUPPORT = 'SUPPORT',
    PROVIDER = 'PROVIDER',
    PATIENT = 'PATIENT',
}

export enum MessagingOrderEnum {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

export enum MessagingSortType {
    CREATED = 'CREATED',
    UPDATED = 'UPDATED',
    OPEN = 'OPEN',
    READ = 'READ',
    SUBJECT = 'SUBJECT',
    AUTHOR = 'AUTHOR',
}

export enum MessagingTagType {
    USER = 'USER',
    SYSTEM = 'SYSTEM',
}

export interface IMessagingAuthor {
    id?: string;
    nickname?: string;
    type?: keyof typeof MessagingAuthorType;
}

export interface IMessagingTag {
    id?: string;
    name?: string;
    type?: keyof typeof MessagingTagType;
    groupId?: string;
    created?: string;
}

export interface IMessagingMessage {
    id?: string;
    subject?: string;
    comments?: IMessagingComment[] | number; // Array from API, number for backward compatibility
    commentCount?: number;
    documents?: number;
    documentCount?: number;
    open?: boolean;
    read?: boolean;
    author?: IMessagingAuthor;
    lastAuthor?: IMessagingAuthor;
    tags?: IMessagingTag[];
    updated?: string;
    created?: string;
    claimPatientFirst?: string;
    claimPatientLast?: string;
    claimGuarantorFirst?: string;
    claimGuarantorLast?: string;
    claimFromDos?: string;
    claimThroughDos?: string;
    claim?: string;
    from?: string;
    purposeType?: string;
    priority?: string;
}

export interface IMessagingComment {
    id?: string;
    body?: string;
    note?: boolean;
    documents?: string[];
    author?: IMessagingAuthor;
    created?: string;
}

export const authorNickname = (message: IMessagingMessage | IMessagingComment | undefined): string => {
    return getNickname(_.get(message, 'author'));
};

export const getNickname = (author: IMessagingAuthor | undefined): string => {
    let nickname = _.get(author, 'nickname');
    const type = _.get(author, 'type');

    if (nickname === 'Business Account' && type === MessagingAuthorType.SUPPORT) {
        return 'Enter Support';
    }

    if (nickname && nickname !== 'Enter, Inc.' && type === MessagingAuthorType.SUPPORT) {
        const pair = _.split(nickname, ' ');
        const firstName = _.first(pair);
        const lastInitial = _.head(_.last(pair));
        nickname = `${firstName} ${lastInitial}.`;
    }
    return nickname || '';
};

export const isLastAuthor = (message: IMessagingMessage, author: IMessagingAuthor): boolean => {
    return _.get(message, 'lastAuthor.id') === _.get(author, 'id');
};

export const sortMessages = (
    messages: IMessagingMessage[],
    sorts: MessagingSortType[] = [],
    orders: MessagingOrderEnum[] = [],
): IMessagingMessage[] => {
    // sort results
    let sortCriteria: string[] = _.map(sorts, (sort: MessagingSortType) => _.toLower(sort));
    if (_.isEmpty(sortCriteria)) {
        sortCriteria.push('updated');
    }
    // NB: extra magic for sorting by author
    sortCriteria = _.map(sortCriteria, sort => (sort === 'author' ? 'author.nickname' : sort));

    // NB: expect both provider/support enums to have 'ASCENDING'
    const orderCriteria = _.map(orders, order => (String(order) === 'ASCENDING' ? 'asc' : 'desc'));
    if (_.isEmpty(orderCriteria)) {
        orderCriteria.push('desc');
    }
    return _.orderBy(messages, sortCriteria, orderCriteria);
};

const MD_AUTOLINK_RE = /<\s*(https?:\/\/|mailto:)[^>]+>/i;

const MD_RE = /(```[\s\S]*?```|(^|\n)#{1,6}\s+|(^|\n)\s*>\s+|(^|\n)\s*([-*+])\s+|(^|\n)\s*\d+\.\s+|\[[^\]]+\]\([^)]+\)|`[^`\n]+`|(\*\*|__)[\s\S]+?\1|(\*|_)[^\s].*?\2)/m;

const HTML_DOC_RE = /<\/?(html|head|body)\b/i;
const HTML_TAG_RE = /<\/?[a-z][a-z0-9-]*\b[^>]*>/i;

// Detect whether text looks like Markdown
export const isMarkdownContent = (text: string): boolean => {
  if (!text) return false;

  // Markdown autolinks like <https://example.com> or <mailto:test@example.com>
  if (MD_AUTOLINK_RE.test(text)) return true;

  // Common markdown syntax signals
  return MD_RE.test(text);
};

// Detect whether text should be treated as HTML
export const isHtmlContent = (text: string): boolean => {
  if (!text) return false;

  // Full HTML documents are always HTML
  if (HTML_DOC_RE.test(text)) return true;

  // If it looks like markdown, prefer markdown over HTML
  if (isMarkdownContent(text)) return false;

  // Exclude markdown-style autolinks
  if (MD_AUTOLINK_RE.test(text)) return false;

  // If it contains real-looking HTML tags, treat as HTML
  return HTML_TAG_RE.test(text);
};

// Normalize fenced code blocks in Markdown so renderers handle them consistently
export const normalizeMarkdown = (text: string): string => {
  if (!text) return text;

  // First, extract and preserve code blocks to avoid processing their content
  const codeBlockPlaceholder = '___CODE_BLOCK_PLACEHOLDER___';
  const codeBlocks: string[] = [];
  let normalized = text.replace(/```([\s\S]*?)```/g, (match, content: string) => {
    const index = codeBlocks.length;
    codeBlocks.push(match);
    return `${codeBlockPlaceholder}${index}${codeBlockPlaceholder}`;
  });

  // Escape underscores in constant/enum-like patterns (all caps with underscores)
  // This prevents markdown from italicizing text like INVALID_MISSING_SUBSCRIBER_INSURED_NAME
  // Matches sequences like: ALL_CAPS_CONSTANT, INVALID_MISSING_SUBSCRIBER_INSURED_NAME
  // Pattern: one or more uppercase letters/numbers, followed by one or more groups of underscore + uppercase letters/numbers
  // Only process content outside of code blocks
  normalized = normalized.replace(/\b[A-Z0-9]+(_[A-Z0-9]+)+\b/g, (match) => {
    return match.replace(/_/g, '\\_');
  });

  // Restore code blocks and normalize them
  normalized = normalized.replace(new RegExp(`${codeBlockPlaceholder}(\\d+)${codeBlockPlaceholder}`, 'g'), (match, index: string) => {
    const codeBlock = codeBlocks[parseInt(index, 10)];
    const innerContent = codeBlock.slice(3, -3); // Remove opening and closing ```
    
    if (!innerContent || !innerContent.trim()) return codeBlock;

    // Check if content starts with a language identifier followed by newline
    const langMatch = innerContent.match(/^([\w-]+)\n([\s\S]*)$/);
    
    if (langMatch) {
      // Has language identifier
      const [, lang, content] = langMatch;
      if (!content.trim()) return codeBlock; // Empty content, keep original
      return `\n\n\`\`\`${lang}\n${content.trim()}\n\`\`\`\n\n`;
    } else {
      // No language identifier, treat entire content as code
      return `\n\n\`\`\`\n${innerContent.trim()}\n\`\`\`\n\n`;
    }
  });

  return normalized;
};

// Very lightweight HTML sanitization (safe only for trusted or semi-trusted input)
export const sanitizeHtml = (html: string): string => {
  if (!html) return html;

  return html
    // Remove all <script> blocks
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove inline event handlers like onclick="", onload="", etc.
    .replace(/\son\w+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '')
    // If a full document is passed, keep only <body> content
    .replace(/^[\s\S]*<body[^>]*>/i, '')
    .replace(/<\/body>[\s\S]*$/i, '')
    .trim();
};

